import * as React from "react"
import { Layer, LayerProps } from "./Layer"
import { Rect } from "../types/Rect"
import { RenderTarget } from "../types/RenderEnvironment"
import { FrameProps, DeprecatedCoreFrameProps, FrameWithMotion } from "./Frame"
import { ComponentPlaceholder, PlaceholderType } from "./ComponentPlaceholder"
import { isReactChild, isReactElement } from "../../utils/type-guards"
import { safeWindow } from "../../utils/safeWindow"
import { NewConstraintProperties, calculateRect, ParentSizeState } from "../types/NewConstraints"
import { Size } from "../../render/types/Size"
import { LayoutMetrics } from "../../render/types/LayoutMetrics"
import { debounce } from "../../render/utils/debounce"
import { MotionStyle } from "framer-motion"
import { runtime } from "../../utils/runtimeInjection"
import { nodeIdFromString } from "../utils/nodeIdFromString"

/**
 * React context used to determine if we're inside a code component.
 * @internal
 */
export const ComponentContainerContext = React.createContext(false)

/**
 * @internal
 */
export interface ComponentContainerProps extends Partial<NewConstraintProperties> {
    style: MotionStyle
    visible: boolean
    componentIdentifier: string
    name?: string
}

/**
 * @internal
 */
export interface ComponentContainerState {
    lastError?: {
        // Used to re-probe component for errors (see render method).
        children: React.ReactNode
        name: string
        message: string
        componentStack: string[]
    }
}

/**
 * @internal
 */
export interface ComponentContainerProperties extends ComponentContainerProps, LayerProps {}

/**
 * @internal
 */
export class ComponentContainer extends Layer<ComponentContainerProperties, ComponentContainerState> {
    static supportsConstraints = true
    state: ComponentContainerState = {}

    static defaultComponentContainerProps: ComponentContainerProps = {
        style: {},
        visible: true,
        componentIdentifier: "",
    }

    static readonly defaultProps: ComponentContainerProperties = {
        ...Layer.defaultProps,
        ...ComponentContainer.defaultComponentContainerProps,
    }

    static contextType = ComponentContainerContext

    contentWrapperRef: HTMLDivElement | null = null

    private setContentWrapperRef = (ref: HTMLDivElement) => {
        // Don't set references for code components rendered inside other code components,
        // because they're not directly selectable on the canvas.
        const inCodeComponent = !!this.context
        if (RenderTarget.current() !== RenderTarget.canvas || inCodeComponent) {
            return
        }

        if (ref) {
            this.contentWrapperRef = ref
        }
    }

    private publishLayoutChange = debounce(() => {
        if (!this.contentWrapperRef) {
            return
        }

        const children = [...this.contentWrapperRef.children]

        const nodeId = nodeIdFromString(this.props.id!)
        const layoutMetrics: LayoutMetrics = {
            ...collectLayoutMetrics(this.contentWrapperRef),
            children: children.map(collectLayoutMetrics),
        }

        runtime.publishLayoutChange({ nodeId, layoutMetrics })
    }, 50)

    componentDidMount() {
        this.publishLayoutChange()
    }

    componentDidUpdate() {
        this.publishLayoutChange()
    }

    componentDidCatch(error: Error, info: React.ErrorInfo) {
        let stack = info.componentStack.split("\n").filter(line => line.length !== 0)
        let currentIndex = 0
        for (const line of stack) {
            if (line.startsWith(`    in ${this.constructor.name}`)) {
                break
            }
            currentIndex++
        }
        stack = stack.slice(0, currentIndex)
        this.setState({
            lastError: {
                children: this.props.children,
                name: error.name,
                message: error.message,
                componentStack: stack,
            },
        })
    }

    renderErrorPlaceholder(title: string, message: string): JSX.Element {
        return (
            <FrameWithMotion {...this.props} background={null}>
                <ComponentPlaceholder type={PlaceholderType.Error} title={title} message={message} />
            </FrameWithMotion>
        )
    }

    render() {
        if (process.env.NODE_ENV !== "production" && safeWindow["perf"]) safeWindow["perf"].nodeRender()
        let { children } = this.props
        const { componentIdentifier } = this.props
        const { lastError: error } = this.state

        // If the file of the component is in has a compile or load error, there will be no children
        // and there will be an error in the componentLoader. If so we render that error.
        // Note, cannot use React.Children.count when children = [null]
        const noChildren = !children || (Array.isArray(children) && children.filter(c => c).length === 0)
        if (noChildren) {
            const errorComponent = runtime.componentLoader.errorForIdentifier(componentIdentifier)
            if (errorComponent) {
                return this.renderErrorPlaceholder(errorComponent.file, errorComponent.error)
            }
        }

        // If an error occurred, componentDidCatch will set error. Additionally, we keep track of the child(ren)
        // reference of this container and only render the error when nothing changed. This means we will
        // re-render the component when something does change, which will either take us out of the error state
        // or update the children reference and keep showing the error. Effectively, this re-probes the component
        // for errors, without throwing an error twice in a row which would make React skip this error boundary
        // and go up the stack.
        if (error && error.children === children) {
            const component = runtime.componentLoader.componentForIdentifier(componentIdentifier)
            const file = !!component ? component.file : "???"
            return this.renderErrorPlaceholder(file, error.message)
        }

        // This is provided by the time budget logic in runtime
        safeWindow["__checkComponentBudget__"]?.()
        let frameProps: Partial<FrameProps & DeprecatedCoreFrameProps> = this.props

        if (RenderTarget.current() !== RenderTarget.canvas) {
            // For Code Overrides, we want the styling properties to be applied to the Frame,
            // and the rest to the actual component
            const {
                left,
                right,
                top,
                bottom,
                center,
                centerX,
                centerY,
                aspectRatio,
                parentSize,
                width,
                height,
                rotation,
                opacity,
                visible,
                _constraints,
                _initialStyle,
                name,
                // Remove the children and the componentIdentifier from the props passed into the component
                componentIdentifier: originalComponentIdentifier,
                children: originalChildren,
                style,
                duplicatedFrom,
                _canMagicMotion,
                ...childProps
            } = frameProps as Partial<FrameProps & DeprecatedCoreFrameProps & ComponentContainerProperties>
            children = React.Children.map(originalChildren, (child: React.ReactElement<typeof childProps>) => {
                if (!isReactChild(child) || !isReactElement(child)) {
                    return child
                }

                return React.cloneElement(child, childProps)
            })
            frameProps = {
                style,
                _constraints,
                _initialStyle,
                left,
                right,
                top,
                bottom,
                center,
                centerX,
                centerY,
                aspectRatio,
                parentSize,
                width,
                height,
                rotation,
                visible,
                name,
                duplicatedFrom,
                id: frameProps.id,
                _canMagicMotion,
            }
        }

        return (
            /* The background should come before the frameProps. It looks like there never should be a background in frameProps,
             * but published design components can contain an old version of the presentation tree that expects the background
             * that is passed to be rendered here
             * See the stackBackgroundTest.tsx integration test for an example of such a case
             */
            <ComponentContainerContext.Provider value={true}>
                <FrameWithMotion background={null} overflow="visible" ref={this.setContentWrapperRef} {...frameProps}>
                    {children}
                </FrameWithMotion>
            </ComponentContainerContext.Provider>
        )
    }
}

;(ComponentContainer as any).rect = function (
    props: Partial<ComponentContainerProperties>,
    parentSize?: Size
): Rect | null {
    return calculateRect(props, parentSize || ParentSizeState.Unknown)
}

function collectLayoutMetrics(element: Element): LayoutMetrics {
    const computedStyle = getComputedStyle(element)

    const metrics: LayoutMetrics = {
        scrollWidth: element.scrollWidth,
        scrollHeight: element.scrollHeight,
        clientLeft: element.clientLeft,
        clientTop: element.clientTop,
        clientWidth: element.clientWidth,
        clientHeight: element.clientHeight,
        computedStyleWidth: computedStyle.width,
        computedStyleHeight: computedStyle.height,
    }

    if (element instanceof HTMLElement) {
        metrics.offsetTop = element.offsetTop
        metrics.offsetLeft = element.offsetLeft
        metrics.offsetWidth = element.offsetWidth
        metrics.offsetHeight = element.offsetHeight
    }

    return metrics
}
